import { normalizeExperienceType } from './experienceTypes';
import { normalizeRuntimeSaveOutcome } from '../../app/runtime/saveOutcome';
import { getLocalizedMessage } from '../../util/useLocalizedNotifications';

function getDraftExperience(draftConfig) {
  return draftConfig?.search?.searchExperience;
}

const REQUIRED_LAYOUT_VIEWS = {
  unified: ['dropdown', 'resultsPage'],
  fullpage: ['searchBox', 'resultsPage'],
  classic: ['dropdown', 'resultsPage'],
};

const REQUIRED_EDITOR_SCREENS = {
  searchBox: ['empty', 'results', 'noResults'],
  dropdown: ['empty', 'results', 'noResults'],
  resultsPage: ['results', 'noResults'],
};

function hasRenderableCanvas(canvas) {
  return Boolean(
    canvas
    && typeof canvas === 'object'
    && Array.isArray(canvas.nodes)
    && canvas.nodes.length > 0,
  );
}

function hasScreenCanvas(viewConfig, screen) {
  return hasRenderableCanvas(viewConfig?.screens?.[screen]?.canvas);
}

function hasRequiredEditorScreens(viewConfig, view) {
  const requiredScreens = REQUIRED_EDITOR_SCREENS[view] || ['results'];
  return requiredScreens.every((screen) => hasScreenCanvas(viewConfig, screen));
}

function hasCompleteLayoutForExperience(config, experience) {
  const views = config?.search?.views;
  const requiredViews = REQUIRED_LAYOUT_VIEWS[experience] || [];
  if (!views || requiredViews.length === 0) {
    return false;
  }

  return requiredViews.every((view) => hasRequiredEditorScreens(views[view], view));
}

function cloneConfig(config) {
  return JSON.parse(JSON.stringify(config));
}

function assertGatewaySaveSucceeded(result, fallbackMessage) {
  const outcome = normalizeRuntimeSaveOutcome(result);
  if (outcome.success && outcome.errors.length === 0) return outcome;
  const firstError = outcome.errors[0];
  const message = firstError?.message
    || firstError?.error
    || result?.error
    || fallbackMessage;
  throw new Error(message);
}

export function createWizardDraftTransport({
  selectedExperience,
  draftConfig,
  onDraftChange,
  searchSettingsClient = null,
  gatewayClient = null,
  localizeMessage = getLocalizedMessage,
}) {
  const settingsClient = searchSettingsClient || gatewayClient;
  let currentDraft = draftConfig || null;
  const currentExperience = normalizeExperienceType(selectedExperience) || 'unified';
  const normalizeDraft = (config) => {
    const draft = cloneConfig(config);
    draft.search = {
      ...(draft.search || {}),
      searchExperience: normalizeExperienceType(getDraftExperience(draft)) || currentExperience,
    };
    return draft;
  };
  const setCurrentDraft = (nextConfig) => {
    currentDraft = normalizeDraft(nextConfig);
    if (onDraftChange) {
      onDraftChange(currentDraft);
    }
    return currentDraft;
  };
  const assertSettingsClient = () => {
    if (!settingsClient) {
      throw new Error(localizeMessage('search.settings.serviceUnavailable'));
    }
  };
  const resolveDraft = async (shopId, mode = 'preview') => {
    if (
      getDraftExperience(currentDraft) === currentExperience
      && hasCompleteLayoutForExperience(currentDraft, currentExperience)
    ) {
      return currentDraft;
    }

    assertSettingsClient();

    if (typeof settingsClient.loadSearchExperienceDefaults !== 'function') {
      throw new Error(localizeMessage('search.settings.defaultsUnavailable'));
    }

    const loaded = await settingsClient.loadSearchExperienceDefaults(shopId, currentExperience, mode);
    return setCurrentDraft(loaded);
  };

  return {
    async loadSearchExperienceConfig(shopId, mode = 'preview') {
      return resolveDraft(shopId, mode);
    },

    async describeSearchExperienceConfig(shopId) {
      assertSettingsClient();
      return settingsClient.describeSearchExperienceConfig(shopId);
    },

    async getSearchComponents(shopId) {
      assertSettingsClient();
      if (typeof settingsClient.getSearchComponents !== 'function') {
        return { success: true, components: {} };
      }
      return settingsClient.getSearchComponents(shopId);
    },

    async validateSearchExperienceConfig(shopId, config) {
      if (typeof settingsClient?.validateSearchExperienceConfig === 'function') {
        return settingsClient.validateSearchExperienceConfig(shopId, config);
      }
      return {
        valid: true,
        errors: [],
      };
    },

    async saveSearchExperienceConfig(shopId, nextConfig, mode = 'preview') {
      assertSettingsClient();
      const draft = normalizeDraft(nextConfig);
      const result = await settingsClient.saveSearchExperienceConfig(shopId, draft, mode);
      assertGatewaySaveSucceeded(result, localizeMessage('search.settings.config.saveRejected'));
      const savedConfig = result?.config || draft;
      setCurrentDraft(savedConfig);
      return savedConfig;
    },

    async copySearchExperienceToProduction(shopId) {
      assertSettingsClient();
      if (typeof settingsClient.loadSearchExperienceConfig !== 'function') {
        throw new Error(localizeMessage('search.settings.experienceUnavailable'));
      }

      const draft = normalizeDraft(await settingsClient.loadSearchExperienceConfig(shopId, 'preview'));
      assertGatewaySaveSucceeded(
        await settingsClient.saveSearchExperienceConfig(shopId, draft, 'production'),
        localizeMessage('search.settings.config.saveRejected'),
      );
      setCurrentDraft(draft);

      return {
        success: true,
        config: draft,
      };
    },

    async persistDraftToGateway(shopId, { production = false } = {}) {
      assertSettingsClient();

      const draft = cloneConfig(await resolveDraft(shopId, 'preview'));
      assertGatewaySaveSucceeded(
        await settingsClient.saveSearchExperienceConfig(shopId, draft, 'preview'),
        localizeMessage('search.settings.config.saveRejected'),
      );

      if (production) {
        assertGatewaySaveSucceeded(
          await settingsClient.saveSearchExperienceConfig(shopId, draft, 'production'),
          localizeMessage('search.settings.config.saveRejected'),
        );
      }

      return {
        success: true,
        config: draft,
      };
    },
  };
}
